import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, finalize, Observable, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionManager {
  // Map to hold each component's request cancellation subjects and DestroyRef
  private componentMap = new Map<
    string,
    {
      destroyRef: DestroyRef;
      requests: Map<string, Subject<void>[]>;
      eventListeners: Map<
        string,
        { element: HTMLElement | (Window & typeof globalThis) | Document; event: string; listener: EventListener }[]
      >;
    }
  >();

  private destroyRef = inject(DestroyRef);

  private callTracker = new Map<string, { count: number; timestamp: number }>();

  // Map to hold debounced request timeouts
  private debouncedTimeouts = new Map<
    string,
    {
      timeout: any;
      latestRequest: { observable: Observable<any>; resolve: (value: Observable<any>) => void };
    }
  >();

  /**
   * Register a component for request management
   * @param componentId The ID of the component
   * @param destroyRef The DestroyRef for the component
   * @returns
   */
  registerComponent(componentId: string, destroyRef: DestroyRef): void {
    if (this.componentMap.has(componentId)) {
      return;
    }

    this.componentMap.set(componentId, {
      destroyRef,
      requests: new Map<string, Subject<void>[]>(),
      eventListeners: new Map<
        string,
        { element: HTMLElement | (Window & typeof globalThis) | Document; event: string; listener: EventListener }[]
      >(),
    });

    destroyRef.onDestroy(() => {
      const componentData = this.componentMap.get(componentId);
      if (componentData) {
        componentData.requests.forEach((subjects: Subject<void>[]) => {
          subjects.forEach((subject: Subject<void>) => subject.next());
        });

        // Clean up event listeners
        componentData.eventListeners.forEach(
          (
            listeners: {
              element: HTMLElement | (Window & typeof globalThis) | Document;
              event: string;
              listener: EventListener;
            }[]
          ) => {
            listeners.forEach(({ element, event, listener }) => {
              element.removeEventListener(event, listener);
            });
          }
        );
      }

      // Clean up any debounced timeouts for this component
      const keysToDelete: string[] = [];
      this.debouncedTimeouts.forEach((_, key) => {
        if (key.startsWith(`${componentId}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => {
        const timeout = this.debouncedTimeouts.get(key);
        if (timeout) {
          clearTimeout(timeout.timeout);
          this.debouncedTimeouts.delete(key);
        }
      });
      this.componentMap.delete(componentId);
    });
  }

  /**
   * Create a cancellable request
   * @param request$ The request observable
   * @param key The request key
   * @param componentId The component ID
   * @param canHaveMultiple Whether multiple requests are allowed
   * @param debounceMs Debounce time in milliseconds (optional)
   * @param useDistinctUntilChanged Whether to apply distinctUntilChanged (optional)
   * @returns Observable of the request
   */
  createRequest<T>(
    request$: Observable<T>,
    key: string,
    componentId: string,
    canHaveMultiple: boolean = false,
    debounceMs?: number,
    useDistinctUntilChanged: boolean = false
  ): Observable<T> {
    // Check if the component is registered
    if (!this.componentMap.has(componentId)) {
      throw new Error(`Component with key ${componentId} is not registered.`);
    }

    const componentData = this.componentMap.get(componentId)!;
    const keyMap = componentData.requests;

    // Cancel previous requests if not allowed multiple
    if (!canHaveMultiple) {
      const trackingKey = `${componentId}:${key}`;
      const now = Date.now();
      const existing = this.callTracker.get(trackingKey);

      // Only warn about duplicates if there's no debouncing (when debouncing is active, duplicates are expected and handled)
      if (existing && now - existing.timestamp < 1000 && !debounceMs) {
        // Within 1 second and no debouncing
        console.warn(`Potential duplicate createRequest call detected:`, {
          componentId,
          key,
          callCount: existing.count + 1,
          timeSinceLastCall: now - existing.timestamp,
          canHaveMultiple: false,
          note: 'Consider using debouncing to handle rapid calls',
        });
      }

      this.callTracker.set(trackingKey, {
        count: existing ? existing.count + 1 : 1,
        timestamp: now,
      });

      this.cancelRequests(key, componentId);
    }

    // If debounce is specified, handle debouncing at the call level
    if (debounceMs) {
      return this.createDebouncedRequest(request$, key, componentId, debounceMs, useDistinctUntilChanged, keyMap);
    }

    // Create and store a new cancellation subject
    const subject = new Subject<void>();
    if (keyMap.has(key)) {
      keyMap.get(key)!.push(subject);
    } else {
      keyMap.set(key, [subject]);
    }

    // Helper function to remove subject from map
    const removeSubjectFromMap = () => {
      const subjects = keyMap.get(key);
      if (subjects) {
        const index = subjects.indexOf(subject);
        if (index > -1) {
          subjects.splice(index, 1);
        }
      }
    };

    if (useDistinctUntilChanged) {
      return request$.pipe(
        takeUntil(subject),
        takeUntilDestroyed(componentData.destroyRef),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        finalize(removeSubjectFromMap)
      );
    } else {
      return request$.pipe(
        takeUntil(subject),
        takeUntilDestroyed(componentData.destroyRef),
        takeUntilDestroyed(this.destroyRef),
        finalize(removeSubjectFromMap)
      );
    }
  }

  /**
   * Create a debounced request that prevents multiple rapid API calls
   * @param request$ The request observable
   * @param key The request key
   * @param componentId The component ID
   * @param debounceMs Debounce time in milliseconds
   * @param useDistinctUntilChanged Whether to apply distinctUntilChanged
   * @param keyMap The request key map for the component
   * @returns Observable of the request
   */
  private createDebouncedRequest<T>(
    request$: Observable<T>,
    key: string,
    componentId: string,
    debounceMs: number,
    useDistinctUntilChanged: boolean,
    keyMap: Map<string, Subject<void>[]>
  ): Observable<T> {
    const debouncedKey = `${componentId}:${key}`;

    return new Observable<T>((observer) => {
      // Clear any existing timeout for this key
      const existingTimeout = this.debouncedTimeouts.get(debouncedKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout.timeout);
        this.debouncedTimeouts.delete(debouncedKey);
      }

      // Create a timeout that will execute the actual request after debounce period
      const timeout = setTimeout(() => {
        this.debouncedTimeouts.delete(debouncedKey);

        // Create and store a new cancellation subject
        const subject = new Subject<void>();
        if (keyMap.has(key)) {
          keyMap.get(key)!.push(subject);
        } else {
          keyMap.set(key, [subject]);
        }

        // Helper function to remove subject from map
        const removeSubjectFromMap = () => {
          const subjects = keyMap.get(key);
          if (subjects) {
            const index = subjects.indexOf(subject);
            if (index > -1) {
              subjects.splice(index, 1);
            }
          }
        };

        const componentData = this.componentMap.get(componentId)!;

        let finalRequest$ = request$.pipe(
          takeUntil(subject),
          takeUntilDestroyed(componentData.destroyRef),
          takeUntilDestroyed(this.destroyRef),
          finalize(removeSubjectFromMap)
        );

        if (useDistinctUntilChanged) {
          finalRequest$ = finalRequest$.pipe(distinctUntilChanged());
        }

        const subscription = finalRequest$.subscribe({
          next: (value) => observer.next(value),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        });

        // Store cleanup function in case of early cancellation
        observer.add(() => {
          subscription.unsubscribe();
          subject.next();
        });
      }, debounceMs);

      // Store the timeout
      this.debouncedTimeouts.set(debouncedKey, {
        timeout,
        latestRequest: {
          observable: request$ as Observable<any>,
          resolve: () => {
            /* no-op */
          },
        },
      });

      // Return cleanup function for the outer observable
      return () => {
        const currentTimeout = this.debouncedTimeouts.get(debouncedKey);
        if (currentTimeout && currentTimeout.timeout === timeout) {
          clearTimeout(timeout);
          this.debouncedTimeouts.delete(debouncedKey);
        }
      };
    });
  }

  /**
   * Get cancellation subjects for a specific key
   * @param key The request key
   * @param componentId The component ID
   * @returns An array of cancellation subjects or undefined
   */
  getCancellationSubject(key: string, componentId: string): Subject<void>[] | undefined {
    if (this.componentMap.has(componentId)) {
      const componentData = this.componentMap.get(componentId)!;
      const keyMap = componentData.requests;
      if (keyMap.has(key)) {
        return keyMap.get(key)!;
      }
    }

    return undefined;
  }

  /**
   * Cancel requests for a specific key
   * @param key The request key
   * @param componentId The component ID
   */
  cancelRequests(key: string, componentId: string): void {
    if (this.componentMap.has(componentId)) {
      const componentData = this.componentMap.get(componentId)!;
      const keyMap = componentData.requests;

      if (keyMap.has(key)) {
        keyMap.get(key)!.forEach((subject: Subject<void>) => subject.next());
        keyMap.delete(key);
      }
    }

    // Also cancel any pending debounced requests for this key
    const debouncedKey = `${componentId}:${key}`;
    const debouncedTimeout = this.debouncedTimeouts.get(debouncedKey);
    if (debouncedTimeout) {
      clearTimeout(debouncedTimeout.timeout);
      this.debouncedTimeouts.delete(debouncedKey);
    }
  }

  /**
   * Cancel all requests for a specific component
   * @param componentId The ID of the component whose requests should be cancelled
   */
  cancelRequestsForComponent(componentId: string): void {
    if (this.componentMap.has(componentId)) {
      const componentData = this.componentMap.get(componentId)!;
      const keyMap = componentData.requests;

      keyMap.forEach((subjects, _key) => {
        subjects.forEach((subject) => subject.next());
      });

      keyMap.clear();
    }

    // Also cancel any pending debounced requests for this component
    const keysToDelete: string[] = [];
    this.debouncedTimeouts.forEach((_, key) => {
      if (key.startsWith(`${componentId}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => {
      const timeout = this.debouncedTimeouts.get(key);
      if (timeout) {
        clearTimeout(timeout.timeout);
        this.debouncedTimeouts.delete(key);
      }
    });
  }

  /**
   * Add an event listener and track it for automatic cleanup
   * @param element The DOM element to attach the event listener to
   * @param event The event type (e.g., 'scroll', 'click')
   * @param listener The event listener function
   * @param key The key to group event listeners
   * @param componentId The component ID
   * @param options Optional event listener options
   */
  addEventListener(
    element: HTMLElement | (Window & typeof globalThis) | Document,
    event: string,
    listener: EventListener,
    key: string,
    componentId: string,
    options?: boolean | AddEventListenerOptions
  ): void {
    // Check if the component is registered
    if (!this.componentMap.has(componentId)) {
      throw new Error(`Component with key ${componentId} is not registered.`);
    }

    const componentData = this.componentMap.get(componentId)!;
    const eventListenersMap = componentData.eventListeners;

    // Remove any existing event listeners for this key before adding the new one
    if (eventListenersMap.has(key)) {
      const existingListeners = eventListenersMap.get(key)!;
      existingListeners.forEach(({ element: existingElement, event: existingEvent, listener: existingListener }) => {
        existingElement.removeEventListener(existingEvent, existingListener);
      });
      eventListenersMap.delete(key);
    }

    // Add the event listener to the element
    element.addEventListener(event, listener, options);

    // Track the event listener for cleanup
    const listenerData = { element, event, listener };
    eventListenersMap.set(key, [listenerData]);
  }

  /**
   * Remove event listeners for a specific key
   * @param key The key identifying the event listeners to remove
   * @param componentId The component ID
   */
  removeEventListeners(key: string, componentId: string): void {
    if (this.componentMap.has(componentId)) {
      const componentData = this.componentMap.get(componentId)!;
      const eventListenersMap = componentData.eventListeners;

      if (eventListenersMap.has(key)) {
        const listeners = eventListenersMap.get(key)!;
        listeners.forEach(({ element, event, listener }) => {
          element.removeEventListener(event, listener);
        });
        eventListenersMap.delete(key);
      }
    }
  }

  /**
   * Remove all event listeners for a specific component
   * @param componentId The component ID
   */
  removeAllEventListeners(componentId: string): void {
    if (this.componentMap.has(componentId)) {
      const componentData = this.componentMap.get(componentId)!;
      const eventListenersMap = componentData.eventListeners;

      eventListenersMap.forEach((listeners) => {
        listeners.forEach(({ element, event, listener }) => {
          element.removeEventListener(event, listener);
        });
      });

      eventListenersMap.clear();
    }
  }
}
